(() => {
  'use strict';
  if (window.RecovrMotion) return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const desktop = window.matchMedia('(min-width: 1000px) and (pointer: fine)');
  const states = new Map();
  const allowsMotion = section => document.body.dataset.rMotion === 'true' && section.dataset.motion !== 'false' && !reduced.matches && !window.Shopify?.designMode;
  function init(section) {
    if (states.has(section)) return;
    const cleanups = [];
    const animations = new Set();
    const on = (el, event, handler, options) => { el.addEventListener(event,handler,options); cleanups.push(() => el.removeEventListener(event,handler,options)); };
    const animate = (el, frames, opts) => {
      if (!el.animate) return;
      const animation = el.animate(frames, opts);
      animations.add(animation);
      animation.finished.then(() => animations.delete(animation)).catch(() => animations.delete(animation));
    };
    let observer;
    if (allowsMotion(section) && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        if (!allowsMotion(section)) return;
        animate(entry.target,[{opacity:0,transform:'translateY(24px)'},{opacity:1,transform:'translateY(0)'}],{duration:650,easing:'cubic-bezier(.22,.7,.24,1)',fill:'none'});
      }), {threshold:0.06,rootMargin:'0px 0px -18px 0px'});
      section.querySelectorAll('[data-r-reveal]').forEach(el => observer.observe(el));
      section.querySelectorAll('[data-r-intro]').forEach((el,i) => animate(el,[{opacity:0,transform:'translateY(22px)'},{opacity:1,transform:'translateY(0)'}],{duration:800,delay:100 + i*85,easing:'cubic-bezier(.22,.7,.24,1)',fill:'backwards'}));
      cleanups.push(() => observer.disconnect());
    }
    let frame = 0;
    let visible = false;
    const depths = [...section.querySelectorAll('[data-r-depth]')];
    const updateDepth = () => {
      frame = 0;
      if (!visible || !desktop.matches || !allowsMotion(section)) return;
      const box = section.getBoundingClientRect();
      const progress = Math.max(-1,Math.min(1,(window.innerHeight/2-(box.top+box.height/2))/(window.innerHeight+box.height)));
      depths.forEach(el => { el.style.transform = `translate3d(0,${(progress*65).toFixed(2)}px,0) scale(1.04)`; });
    };
    const queueDepth = () => { if (visible && !frame && desktop.matches && allowsMotion(section)) frame = requestAnimationFrame(updateDepth); };
    if (depths.length && 'IntersectionObserver' in window) {
      const depthObserver = new IntersectionObserver(entries => { visible = entries.some(e => e.isIntersecting); if (visible) queueDepth(); },{rootMargin:'80px'});
      depthObserver.observe(section);
      on(window,'scroll',queueDepth,{passive:true});
      on(window,'resize',queueDepth,{passive:true});
      cleanups.push(() => { depthObserver.disconnect(); cancelAnimationFrame(frame); depths.forEach(el => el.style.removeProperty('transform')); });
    }
    const track = section.querySelector('[data-r-track]');
    if (track) {
      const prev = section.querySelector('[data-r-prev]');
      const next = section.querySelector('[data-r-next]');
      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        if (prev) prev.disabled = track.scrollLeft <= 3;
        if (next) next.disabled = max <= 3 || track.scrollLeft >= max-3;
      };
      const move = direction => {
        const card = track.firstElementChild;
        const gap = parseFloat(getComputedStyle(track).gap) || 0;
        track.scrollBy({left:direction*((card?.getBoundingClientRect().width || track.clientWidth)+gap),behavior:reduced.matches ? 'auto' : 'smooth'});
      };
      if (prev) on(prev,'click',() => move(-1));
      if (next) on(next,'click',() => move(1));
      on(track,'keydown',event => {
        if (event.target !== track || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
        event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
      });
      on(track,'scroll',update,{passive:true});
      if ('ResizeObserver' in window) { const resizeObserver = new ResizeObserver(update); resizeObserver.observe(track); cleanups.push(() => resizeObserver.disconnect()); }
      else on(window,'resize',update,{passive:true});
      on(document,'shopify:block:select',event => { if (track.contains(event.target)) { track.scrollTo({left:event.target.offsetLeft-track.offsetLeft,behavior:'auto'}); } });
      update();
    }
    section.querySelectorAll('video').forEach(video => {
      const toggle = section.querySelector('[data-r-video-toggle]');
      let manuallyPaused = false;
      let inView = false;
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      const sync = () => {
        if (!toggle) return;
        toggle.setAttribute('aria-label',video.paused ? toggle.dataset.playLabel : toggle.dataset.pauseLabel);
        const symbol=toggle.querySelector('[data-r-video-symbol]'); if(symbol) symbol.textContent=video.paused ? '▷' : 'Ⅱ';
      };
      const play = () => { const promise=video.play(); if (promise?.catch) promise.catch(sync); };
      const update = () => { if(inView && !manuallyPaused && !reduced.matches && !document.hidden) play(); else video.pause(); };
      on(video,'play',sync);on(video,'pause',sync);on(document,'visibilitychange',update);
      if(toggle) on(toggle,'click',() => { manuallyPaused=!video.paused; if(manuallyPaused) video.pause(); else play(); });
      if('IntersectionObserver' in window){const vo=new IntersectionObserver(entries=>{inView=entries.some(e=>e.isIntersecting);update();},{threshold:.05});vo.observe(video);cleanups.push(()=>vo.disconnect());}
      else { inView=true;update(); }
      cleanups.push(()=>video.pause());sync();
    });
    states.set(section,() => { cleanups.forEach(fn=>fn());animations.forEach(a=>a.cancel());states.delete(section); });
  }
  const mount = (root=document) => { if(root.matches?.('[data-r-section]'))init(root);root.querySelectorAll('[data-r-section]').forEach(init); };
  const reset = () => { [...states.values()].forEach(dispose=>dispose());mount(); };
  mount();
  document.addEventListener('shopify:section:load',event=>mount(event.target));
  document.addEventListener('shopify:section:unload',event=>{[...states.keys()].forEach(section=>{if(event.target.contains(section))states.get(section)();});});
  reduced.addEventListener('change',reset);desktop.addEventListener('change',reset);
  window.RecovrMotion={refresh:reset};
})();
